import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import styles from './UpdatesTabContent.module.css';
import { fetchSwagUpdateDetails, deleteSwagUpdate, updateSwagUpdate } from '../services/unitService';
import { useCachedPermissions } from '../hooks/useCachedPermissions';
import { showErrorNotification } from '../store/slices/notificationSlice';
import Dialog from './Dialog';

export default function SwagUpdateDetails({ postId, onDelete, onEdit }) {
    const dispatch = useDispatch();

    // Swag updates permissions
    const { hasDeleteSwagUpdates, hasUpdateSwagUpdates, permissionsLoading } = useCachedPermissions();

    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editMode, setEditMode] = useState(false);
    const [editTitle, setEditTitle] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [keptLogo, setKeptLogo] = useState(''); // URL of kept logo
    const [keptImages, setKeptImages] = useState([]); // URLs of kept images
    const [editLogo, setEditLogo] = useState(null); // {file, src} for new logo
    const [editImages, setEditImages] = useState([]); // [{file, src}] for new images
    const [editLoading, setEditLoading] = useState(false);
    const [editError, setEditError] = useState('');
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [selectedImage, setSelectedImage] = useState(null); // for image modal

    useEffect(() => {
        setLoading(true);
        setError(null);
        fetchSwagUpdateDetails(postId)
            .then(res => {
                const data = res.data;
                console.log('[DETAILS] fetched:', data);
                setDetails(data);
                setEditTitle(data.title || '');
                setEditDescription(data.description || '');
                setKeptLogo(data.logo || '');
                let imgs = [];
                if (Array.isArray(data.images) && data.images.length > 0) {
                    imgs = [...data.images];
                } else if (Array.isArray(data.logos) && data.logos.length > 0) {
                    imgs = [...data.logos];
                }
                setKeptImages(imgs);
                setEditLogo(null);
                setEditImages([]);
                setLoading(false);
            })
            .catch(err => {
                setError(err.message || 'Failed to load details');
                setLoading(false);
            });
    }, [postId]);

    const handleDelete = async () => {
        if (!hasDeleteSwagUpdates) {
            dispatch(showErrorNotification('You do not have permission to delete swag updates'));
            return;
        }
        if (!window.confirm('Are you sure you want to delete this swag update?')) return;
        setDeleteLoading(true);
        setDeleteError('');
        try {
            await deleteSwagUpdate(postId);
            if (onDelete) onDelete(postId);
        } catch (err) {
            setDeleteError(err.message);
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleEdit = () => {
        if (!hasUpdateSwagUpdates) {
            dispatch(showErrorNotification('You do not have permission to update swag updates'));
            return;
        }
        setEditMode(true);
        setEditError('');
    };

    const handleCancelEdit = () => {
        setEditMode(false);
        setEditTitle(details.title || '');
        setEditDescription(details.description || '');
        setEditLogo(null);
        setEditImages([]);
        setEditError('');
    };

    const handleSaveEdit = async () => {
        setEditLoading(true);
        setEditError('');
        try {
            const logo = editLogo ? editLogo.file : keptLogo;
            const images = editImages.map(img => img.file);
            const old_img = keptImages;
            console.log('[UPDATE] title:', editTitle);
            console.log('[UPDATE] description:', editDescription);
            console.log('[UPDATE] logo:', logo);
            console.log('[UPDATE] images (new):', images);
            console.log('[UPDATE] old_img:', old_img);
            const res = await updateSwagUpdate(postId, {
                title: editTitle,
                description: editDescription,
                logo,
                images,
                old_img
            });
            // After update, refetch details
            const detailsRes = await fetchSwagUpdateDetails(postId);
            setDetails(detailsRes.data);
            setKeptLogo(detailsRes.data.logo || '');
            let imgs = [];
            if (Array.isArray(detailsRes.data.images) && detailsRes.data.images.length > 0) {
                imgs = [...detailsRes.data.images];
            } else if (Array.isArray(detailsRes.data.logos) && detailsRes.data.logos.length > 0) {
                imgs = [...detailsRes.data.logos];
            }
            setKeptImages(imgs);
            setEditMode(false);
            if (onEdit) onEdit(detailsRes.data);
        } catch (err) {
            setEditError(err.message);
        } finally {
            setEditLoading(false);
        }
    };


    const handleLogoUpload = () => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'image/*';
        fileInput.onchange = (e) => {
            const file = e.target.files[0];
            if (file) {
                const reader = new FileReader();
                reader.onload = (event) => {
                    setEditLogo({ file, src: event.target.result });
                };
                reader.readAsDataURL(file);
            }
        };
        fileInput.click();
    };

    const handleImagesUpload = () => {
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'image/*';
        fileInput.multiple = true;
        fileInput.onchange = (e) => {
            const files = Array.from(e.target.files);
            console.log('[IMAGES UPLOAD] files:', files);
            const readers = files.map(file => {
                return new Promise(resolve => {
                    const reader = new FileReader();
                    reader.onload = (event) => {
                        resolve({ file, src: event.target.result });
                    };
                    reader.readAsDataURL(file);
                });
            });
            Promise.all(readers).then(newImgs => {
                console.log('[IMAGES UPLOAD] previews:', newImgs);
                setEditImages(prev => [...prev, ...newImgs]);
            });
        };
        fileInput.click();
    };

    const handleRemoveKeptImage = (idx) => {
        console.log('[REMOVE KEPT IMAGE] idx:', idx, 'img:', keptImages[idx]);
        setKeptImages(prev => prev.filter((_, i) => i !== idx));
    };
    const handleRemoveNewImage = (idx) => {
        console.log('[REMOVE NEW IMAGE] idx:', idx, 'img:', editImages[idx]);
        setEditImages(prev => prev.filter((_, i) => i !== idx));
    };

    const handleImageClick = (src) => {
        setSelectedImage(src);
    };

    const handleCloseModal = () => {
        setSelectedImage(null);
    };

    if (loading) {
        return <div className={styles.noDataMessage}><p>Loading...</p></div>;
    }
    if (error) {
        return <div className={styles.noDataMessage}><p>{error}</p></div>;
    }
    if (!details) {
        return <div className={styles.noDataMessage}><p>No details found.</p></div>;
    }



    return (
        <div className={styles.updatesContainer}>
            <div className={styles.headerContainer}>
                <div className={styles.headerLeft}>
                    <h2 className={styles.headerTitle}>Swag Update Details</h2>
                </div>
                <div className={styles.headerActions}>
                    {hasDeleteSwagUpdates && (
                        <button className={styles.deleteButton} onClick={handleDelete} disabled={editLoading || loading || deleteLoading}>
                            {deleteLoading ? 'Deleting...' : 'Delete'}
                        </button>
                    )}
                    {editMode ? (
                        <>
                            <button className={styles.saveButton} onClick={handleSaveEdit} disabled={editLoading || loading || deleteLoading}>{editLoading ? 'Saving...' : 'Save'}</button>
                            <button className={styles.cancelButton} onClick={handleCancelEdit} disabled={editLoading || loading || deleteLoading}>Cancel</button>
                        </>
                    ) : (
                        hasUpdateSwagUpdates && (
                            <button className={styles.saveButton} onClick={handleEdit} disabled={editLoading || loading || deleteLoading}>Edit</button>
                        )
                    )}
                </div>
            </div>
            {editError && <div className={styles.formError}>{editError}</div>}
            {deleteError && <div className={styles.formError}>{deleteError}</div>}
            <div className={styles.unitUpdateDetailsCard}>
                <div className={styles.unitUpdateImages}>
                    {editMode ? (
                        <>
                            <div style={{ marginBottom: 24 }}>
                                <label className={styles.unitUpdateLabel}>Logo</label>
                                {editLogo ? (
                                    <div style={{ position: 'relative', display: 'inline-block' }}>
                                        <img src={editLogo.src} alt="New Logo" className={styles.unitUpdateImage} style={{ maxWidth: 200, maxHeight: 200, borderRadius: 8, background: '#222' }} />
                                        <button type="button" className={styles.removeButton} style={{ position: 'absolute', top: 4, right: 4 }} onClick={() => setEditLogo(null)} disabled={editLoading}>Remove</button>
                                    </div>
                                ) : keptLogo ? (
                                    <div style={{ position: 'relative', display: 'inline-block' }}>
                                        <img src={keptLogo} alt="Current Logo" className={styles.unitUpdateImage} style={{ maxWidth: 200, maxHeight: 200, borderRadius: 8, background: '#222' }} />
                                        <button type="button" className={styles.removeButton} style={{ position: 'absolute', top: 4, right: 4 }} onClick={() => setKeptLogo('')} disabled={editLoading}>Remove</button>
                                        <button type="button" style={{ position: 'absolute', bottom: 4, left: 4, background: '#eed275', color: '#222', border: 'none', padding: '2px 8px', borderRadius: 4, fontSize: 12, cursor: 'pointer' }} onClick={handleLogoUpload} disabled={editLoading}>Change</button>
                                    </div>
                                ) : (
                                    <button type="button" style={{ background: '#eed275', color: '#222', border: 'none', padding: '8px 16px', borderRadius: 4, cursor: 'pointer' }} onClick={handleLogoUpload} disabled={editLoading}>Upload Logo</button>
                                )}
                            </div>
                            <div>
                                <label className={styles.unitUpdateLabel}>Images</label>
                                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                                    {keptImages.map((img, idx) => (
                                        <div key={idx} style={{ position: 'relative' }}>
                                            <img src={img} alt={`Image ${idx + 1}`} className={styles.unitUpdateImage} style={{ maxWidth: 200, maxHeight: 200, borderRadius: 8, background: '#222' }} />
                                            <button
                                                type="button"
                                                className={styles.removeButton}
                                                style={{ position: 'absolute', top: 4, right: 4 }}
                                                onClick={() => handleRemoveKeptImage(idx)}
                                                disabled={editLoading}
                                            >Remove</button>
                                        </div>
                                    ))}
                                    {editImages.map((img, idx) => (
                                        <div key={idx} style={{ position: 'relative' }}>
                                            <img src={img.src} alt={`New Image ${idx + 1}`} className={styles.unitUpdateImage} style={{ maxWidth: 200, maxHeight: 200, borderRadius: 8, background: '#222' }} />
                                            <button type="button" className={styles.removeButton} style={{ position: 'absolute', top: 4, right: 4 }} onClick={() => handleRemoveNewImage(idx)} disabled={editLoading}>Remove</button>
                                        </div>
                                    ))}
                                    <div style={{ position: 'relative' }}>
                                        <button type="button" className={styles.uploadButton} onClick={handleImagesUpload} disabled={editLoading} style={{ width: 200, height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16 }}>
                                            + Add Images
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                            {keptLogo && (
                                <img src={keptLogo} alt="Logo" className={styles.unitUpdateImage} style={{ maxWidth: 200, maxHeight: 200, borderRadius: 8, background: '#222', cursor: 'pointer' }} onClick={() => handleImageClick(keptLogo)} />
                            )}
                            {keptImages.map((img, idx) => (
                                <img key={idx} src={img} alt={`Image ${idx + 1}`} className={styles.unitUpdateImage} style={{ maxWidth: 200, maxHeight: 200, borderRadius: 8, background: '#222', cursor: 'pointer' }} onClick={() => handleImageClick(img)} />
                            ))}
                        </div>
                    )}
                </div>
                <div className={styles.unitUpdateInfo}>
                    <div className={styles.formGroup}>
                        <span className={styles.unitUpdateLabel}>Title</span>
                        {editMode ? (
                            <input
                                className={styles.editTextarea}
                                value={editTitle}
                                onChange={e => setEditTitle(e.target.value)}
                                type="text"
                                disabled={editLoading}
                                placeholder="Enter title..."
                                style={{ width: '100%' }}
                            />
                        ) : (
                            <h3 className={styles.unitCardTitle}>{details.title}</h3>
                        )}
                    </div>
                    <div className={styles.formGroup}>
                        <span className={styles.unitUpdateLabel}>Description</span>
                        {editMode ? (
                            <textarea
                                className={styles.editTextarea}
                                value={editDescription}
                                onChange={e => setEditDescription(e.target.value)}
                                rows={6}
                                disabled={editLoading}
                                placeholder="Enter description..."
                                style={{ width: '100%' }}
                            />
                        ) : (
                            <p className={styles.unitCardDesc}>{details.description}</p>
                        )}
                    </div>
                    <div className={styles.unitUpdateMeta}>
                        <div>
                            <span className={styles.unitUpdateLabel}>Date</span>
                            <span className={styles.unitUpdateDate}>{details.created_date}</span>
                        </div>
                        <div>
                            <span className={styles.unitUpdateLabel}>Time</span>
                            <span className={styles.unitUpdateTime}>{details.created_time}</span>
                        </div>
                    </div>
                </div>
            </div>
            {selectedImage && (
                <Dialog onClose={handleCloseModal}>
                    <img src={selectedImage} alt="Full view" style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain' }} />
                </Dialog>
            )}
        </div>
    );
} 